import {
  type Component,
  createEffect,
  createMemo,
  createSignal,
  Show,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { FreelanceCard } from "@/domains/company/active-search/components/FreelanceCard";
import { cn } from "@/libs/cn";
import type { PoolMatchFreelancer } from "../../api/types";
import { useTalentPool } from "../../context/talent-pool-provider";
import {
  derivePoolCardPresentation,
  poolMatchToSearchResult,
} from "../../utils/match-card-presentation";

export type PoolMatchCardProps = {
  item: PoolMatchFreelancer;
  // The route owns the `FreelanceSheet`; the card reports whether contacting must
  // be disabled (non-discoverable → true, D9) so the route sets `disableContact`.
  onViewProfile: (
    item: PoolMatchFreelancer,
    opts: { disableContact: boolean },
  ) => void;
};

/**
 * Pool tab card — wraps the active-search `FreelanceCard` (heart/pill/badge come
 * free via the provider + `seedFromMatch`; `already_applied` badge free, AC13)
 * and layers the three presentation states (`derivePoolCardPresentation`):
 *   - `normal`      — as-is;
 *   - `unavailable` — attenuated + "non più disponibile" hint; the sheet still
 *     opens (profile viewable) but with contact disabled (D9/OQ4);
 *   - `removed`     — attenuated + "Rimosso dal pool — sparirà al prossimo
 *     aggiornamento" microcopy after an un-heart (AC12), so the card that lingers
 *     until the refetch doesn't read as a bug.
 */
export const PoolMatchCard: Component<PoolMatchCardProps> = (props) => {
  const { t } = useI18n();
  const tp = useTalentPool();

  // Latch: once the membership entry appears (seed resolved) stay `true`. This is
  // what lets a later un-heart (entry → undefined) read as `removed` rather than a
  // false "never saved" — see `derivePoolCardPresentation`.
  const [seeded, setSeeded] = createSignal(false);
  createEffect(() => {
    if (tp.entry(props.item.id) !== undefined) setSeeded(true);
  });

  const presentation = createMemo(() =>
    derivePoolCardPresentation(
      tp.entry(props.item.id),
      props.item.is_discoverable,
      seeded(),
    ),
  );

  return (
    <div class="relative w-full" data-presentation={presentation()}>
      <Show when={presentation() === "removed"}>
        <p class="mb-1 text-xs font-medium text-amber-600">
          {t("company.talent_pool.match.removed_hint")}
        </p>
      </Show>
      <Show when={presentation() === "unavailable"}>
        <p class="mb-1 text-xs font-medium text-muted-foreground">
          {t("company.talent_pool.match.unavailable_hint")}
        </p>
      </Show>
      <div
        class={cn(
          presentation() !== "normal" &&
            "opacity-60 grayscale transition-opacity",
        )}
      >
        <FreelanceCard
          freelancer={poolMatchToSearchResult(props.item)}
          onViewProfile={() =>
            props.onViewProfile(props.item, {
              disableContact: presentation() === "unavailable",
            })
          }
        />
      </div>
    </div>
  );
};
